import { MdOutlineLogin } from "react-icons/md";
import "./Login.css";
import { toast } from "react-hot-toast";
import { useContext, useEffect, useState } from "react";
import { IoMdKey } from "react-icons/io";
import DXLogoBig from "../../utils/images/DXLogoBig.png";
import LoginUser from "../../models/LoginUser";
import { doGet, doPost } from "../../utils/HTTPRequests";
import { globals } from "../../utils/Globals";
import { ActionType, UserContext } from "../Contexts/UserContext";
import { LoginActionType, LoginContext } from "../Contexts/LoginContext";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import Modal from "../Modals/Modal/Modal";
import ForgotPassword from "./ForgotPassword/ForgotPassword";
import ChangePassword from "../AccountDashboard/ChangePassword/ChangePassword";
import { clearItemFromStorage, getParamFromURL } from "../../utils/utilsFuncs";
import useMasterDashboardNavbar from "../../hooks/useMasterDashboardNavbar";
import { getAuthLogo } from "../../utils/logo/logo";

export const getUserPrivileges = async (token, loggedInUserId) => {
  let userId;
  if (loggedInUserId === undefined) {
    userId = getParamFromToken("userId", token);
  } else {
    userId = loggedInUserId;
  }
  return await doGet(globals.user.getUserPrivileges + "/" + userId).then((data) => {
    let privilegesArray = [];
    for (let i = 0; i < data.length; i++) {
      privilegesArray.push(data[i].security_id);
    }
    return privilegesArray;
  });
};

export const getParamFromToken = (param, token) => {
  try {
    return JSON.parse(JSON.stringify(jwtDecode(token)))[param];
  } catch (e) {
    return undefined;
  }
};

function Login() {
  const MFA_CODE_LENGTH = 6;
  const MFA_AUTHENTICATION_FAILED_STATUS_CODE = 430;
  const TEMP_PASSWORD_LOGIN_STATUS_CODE = 431;

  const { dispatch } = useContext(UserContext);
  const loginContext = useContext(LoginContext);

  const { getFirstAccessibleRoute } = useMasterDashboardNavbar();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [isCheckMfaEnable, setIsCheckMfaEnable] = useState(false);
  const [isMfaEnable, setIsMfaEnable] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(DXLogoBig);

  const navigate = useNavigate();

  const notify = (message, duration, icon, style) => {
    toast.error(message, {
      duration: duration,
      icon: icon,
      style: style,
    });
  };

  const notifyFailedLogin = () => {
    setPassword("");
    notify("Incorrect username or password", 5000, "❌", { maxWidth: "800px" });
  };

  const initCodeInputs = () => {
    const inputElements = [...document.querySelectorAll("input.mfa-code-input")];
    inputElements.forEach((element, index) => {
      element.addEventListener("keydown", (e) => {
        // if the keycode is backspace & the current field is empty
        // focus the input before the current. Then the event happens
        // which will clear the "before" input box.
        if (e.keyCode === 8 && e.target.value === "") inputElements[Math.max(0, index - 1)].focus();
      });
      element.addEventListener("input", (e) => {
        // take the first character of the input
        const [first, ...rest] = e.target.value;
        e.target.value = first ?? ""; // first will be undefined when backspace was entered, so set the input to ""
        const lastInputBox = index === inputElements.length - 1;
        const didInsertContent = first !== undefined;
        if (didInsertContent && !lastInputBox) {
          // continue to input the rest of the string
          inputElements[index + 1].focus();
          inputElements[index + 1].value = rest.join("");
          inputElements[index + 1].dispatchEvent(new Event("input"));
        }
      });
    });
  };

  const getMfaCode = () => {
    const inputElements = [...document.querySelectorAll("input.mfa-code-input")];
    return inputElements.map(({ value }) => value).join("");
  };

  const isUserEnableMfa = async (usernameParam, isInitialPasswordFromLink) => {
    await doPost(globals.user.isMfaEnabled, { username: usernameParam === undefined ? userName : usernameParam }, true)
      .then((response) => {
        const data = response.data;
        const mfaEnabled = data.mfaEnabled;
        const accountLogoDetails = data.accountLogoDetails;
        const accountId = accountLogoDetails.accountId;
        const isAccountClosed = accountLogoDetails.closed;
        const progCycleId = accountLogoDetails.progCycleId;

        setIsMfaEnable(mfaEnabled);
        setIsCheckMfaEnable(true);
        setCompanyLogo(getAuthLogo(accountId, isAccountClosed, progCycleId));

        if (mfaEnabled) {
          initCodeInputs();
          document.getElementById("login-container").style.height = "435px";
        } else {
          if (isInitialPasswordFromLink) document.getElementById("login-submit-btn").click();
          document.getElementById("login-container").style.height = "350px";
        }

        const passwordInput = document.getElementById("login-password-input");
        if (passwordInput !== null) passwordInput.focus();
      })
      .catch((error) => {
        setIsCheckMfaEnable(false);
        setUserName("");
      });
  };

  const setInitialPasswordFromLink = (username, tempPassword) => {
    setUserName(username);
    setPassword(tempPassword);
    isUserEnableMfa(username, true);
  };

  const login = async (e) => {
    e.preventDefault();
    let user = new LoginUser();

    user.username = userName;
    user.password = password;
    if (isMfaEnable) user.mfaCode = getMfaCode();

    await doPost(globals.login.login, user, false)
      .then(async (response) => {
        const responseStatus = response.status;

        if (responseStatus === MFA_AUTHENTICATION_FAILED_STATUS_CODE) {
          notify("Incorrect " + MFA_CODE_LENGTH + " digits authenticator code", 5000, "❌", { maxWidth: "800px" });
          return;
        } else if (responseStatus === TEMP_PASSWORD_LOGIN_STATUS_CODE) {
          setShowChangePassword(true);
          return;
        }

        const responseBody = response.data;
        const accountId = responseBody.accountId;
        const token = responseBody.token;
        const userRole = responseBody.role;
        const userPrivileges = responseBody.privileges;
        let redirectToCustomerDashboard = responseBody.redirectToCustomerDashboard;

        let firstAccessibleRoute = getFirstAccessibleRoute(userPrivileges, userRole);

        loginContext.dispatch({
          type: LoginActionType.SET_LOGGED_IN,
          payload: { token: token, privileges: userPrivileges },
        });

        if (accountId !== undefined && redirectToCustomerDashboard) {
          // Customer dashboard
          dispatch({
            type: ActionType.setAccountId,
            payload: { accountId: accountId },
          });

          navigate(`/dashboard/${accountId}`);
        } else {
          navigate(`/masterDashboard` + firstAccessibleRoute);
        }
      })
      .catch((error) => {
        notifyFailedLogin();
        console.log("Login Failed: ", error);
      });
  };

  useEffect(() => {
    loginContext.dispatch({
      type: LoginActionType.SET_LOGGED_OUT,
    });

    const username = getParamFromURL("username");
    const tempPassword = getParamFromURL("tempPass");
    if (username !== null && tempPassword !== null) {
      window.history.replaceState({}, document.title, "/");
      setInitialPasswordFromLink(username, tempPassword);
    }
  }, []);

  return (
    <div className="Login">
      <div className="wrapper">
        <div className="login-logo-container">
          <img src={companyLogo} alt="logo-img" className="login-company-logo" />
        </div>
        <div id="login-container" className="login-container">
          <div className="login-form-section">
            <div className="login-headline">
              <div className="login-title">Login</div>
            </div>
            <hr className="dividing-line" />
            <form className="login-form" onSubmit={(e) => login(e)}>
              <input
                className="login-form-input"
                name="username"
                placeholder="username"
                type="text"
                title="Your user name is usually your phone number."
                value={userName}
                onChange={(e) => {
                  setUserName(e.target.value);
                }}
                required
              />
              {isCheckMfaEnable && (
                <input
                  id="login-password-input"
                  className="login-form-input"
                  name="password"
                  placeholder="password"
                  type="password"
                  maxLength={20}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  required
                />
              )}
              {isCheckMfaEnable && isMfaEnable && (
                <>
                  <div className="mfa-code-title-text">{MFA_CODE_LENGTH} digits authenticator code:</div>
                  <div className="mfa-code-input-container">
                    <input name="mfa-code" className="mfa-code-input" required />
                    <input name="mfa-code" className="mfa-code-input" required />
                    <input name="mfa-code" className="mfa-code-input" required />
                    <input name="mfa-code" className="mfa-code-input" required />
                    <input name="mfa-code" className="mfa-code-input" required />
                    <input name="mfa-code" className="mfa-code-input" required />
                  </div>
                </>
              )}
              <div
                className="forgot-password"
                onClick={() => {
                  clearItemFromStorage("jwtToken");
                  setShowForgotPasswordModal(true);
                }}
              >
                <IoMdKey className="forgot-password-icon" size={15} />
                Forgot Password?
              </div>
              {isCheckMfaEnable ? (
                <button id="login-submit-btn" type="submit" className="login-submit-btn">
                  <MdOutlineLogin className="login-icon" /> Log in
                </button>
              ) : (
                <button
                  className="login-submit-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    isUserEnableMfa();
                  }}
                >
                  <MdOutlineLogin className="login-icon" /> Continue
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
      {showForgotPasswordModal && (
        <Modal
          content={
            <ForgotPassword
              usernameFromLoginScreen={userName}
              setShowForgotPasswordModal={setShowForgotPasswordModal}
            />
          }
          size={"small"}
          modalId={"ForgotPassword"}
          title={"Reset Password"}
          closeModal={() => setShowForgotPasswordModal(false)}
          extraModalStyle={{ height: "50%" }}
        />
      )}
      {showChangePassword && (
        <Modal
          content={
            <ChangePassword
              username={userName}
              setShowChangePassword={setShowChangePassword}
              isTempPassword={true}
              currentTempPassword={password}
              afterChangePassword={() => {
                setPassword("");
              }}
            />
          }
          size={"small"}
          modalId={"ChangePassword"}
          title={"Change Password"}
          closeModal={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
}

export default Login;
